import { BUSINESS_HOURS_DAYS, sanitizeBusinessHours } from "@/lib/businessHours";
import { WEB_BASE_URL } from "@/lib/config/brand";
import type { Business, BusinessHoursDay } from "@/lib/types";
import type { PublicSearchLanding } from "./publicSearchLandingSeo";
import {
  SOKO_GRID_EMAIL,
  SOKO_GRID_PHONE_DISPLAY,
  SOKO_GRID_WEBSITE_URL,
} from "@/src/config/contact";

export const SEO_HOME_TITLE =
  "Soko Grid Marketplace | Kenyan Business Directory & Online Storefronts";

export const SEO_HOME_DESCRIPTION =
  "Soko Grid is a Kenyan business directory and online storefront platform for shops, freelancers, and online businesses to showcase products and services, location, phone, WhatsApp, maps, and social links.";

export const SEO_DEFAULT_IMAGE_URL = buildCanonicalUrl("/pwa-icon-512.png");

const MAX_META_DESCRIPTION_LENGTH = 155;

function cleanString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function firstString(...values: unknown[]): string {
  for (const value of values) {
    const clean = cleanString(value);
    if (clean) return clean;
  }

  return "";
}

function compactObject<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, item]) => {
      if (item == null) return false;
      if (typeof item === "string") return item.trim().length > 0;
      if (Array.isArray(item)) return item.length > 0;
      if (typeof item === "object") return Object.keys(item).length > 0;
      return true;
    })
  ) as T;
}

function uniqueStrings(values: (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  values.forEach((value) => {
    const clean = cleanString(value);
    if (!clean || seen.has(clean)) return;

    seen.add(clean);
    out.push(clean);
  });

  return out;
}

function normalizePath(path: string): string {
  const cleanPath = `/${String(path || "").replace(/^\/+/g, "")}`;

  return cleanPath === "/" ? "/" : cleanPath.replace(/\/+$/g, "");
}

function toAbsoluteUrl(value: unknown): string {
  const clean = cleanString(value);
  if (!clean) return "";
  if (/^https?:\/\//i.test(clean)) return clean;
  if (clean.startsWith("/")) return `${WEB_BASE_URL}${clean}`;

  return "";
}

function truncateDescription(value: string): string {
  const clean = value.replace(/\s+/g, " ").trim();
  if (clean.length <= MAX_META_DESCRIPTION_LENGTH) return clean;

  return `${clean.slice(0, MAX_META_DESCRIPTION_LENGTH - 1).trimEnd()}...`;
}

function getBusinessName(business: Business): string {
  return firstString(
    business.name,
    business.businessName,
    business.shopName,
    "Business"
  );
}

function getBusinessDescription(business: Business): string {
  return firstString(
    business.shortDescription,
    business.description,
    business.about,
    business.aboutText,
    business.aboutUs
  );
}

function getBusinessCategoryLine(business: Business): string {
  const category = firstString(business.categoryName, business.category);
  const subcategory = firstString(
    business.subcategoryName,
    business.subCategoryText,
    Array.isArray(business.subCategory) ? business.subCategory[0] : ""
  );

  return [category, subcategory].filter(Boolean).join(" | ");
}

function getBusinessLocationLine(business: Business): string {
  const area = firstString(
    business.areaName,
    business.location?.areaName,
    business.location?.areaSuggestionName
  );
  const city = firstString(
    business.cityName,
    business.location?.cityName,
    business.location?.citySuggestionName
  );
  const county = firstString(business.countyName, business.location?.countyName);

  return [area, city, county].filter(Boolean).join(", ");
}

function getBusinessMetaDescription(business: Business): string {
  const realDescription = getBusinessDescription(business);

  if (realDescription) return truncateDescription(realDescription);

  const detailLine = [getBusinessCategoryLine(business), getBusinessLocationLine(business)]
    .filter(Boolean)
    .join(" in ");

  if (detailLine) {
    return truncateDescription(`${getBusinessName(business)} on Soko Grid: ${detailLine}.`);
  }

  return `${getBusinessName(business)} on Soko Grid.`;
}

function getBusinessImages(business: Business): string[] {
  const itemImages = Array.isArray(business.items)
    ? business.items.map((item) =>
        toAbsoluteUrl(
          item.imageUrl ||
            item.imageUri ||
            item.imageURI ||
            item.photoUrl ||
            item.img ||
            item.image
        )
      )
    : [];

  return uniqueStrings([
    toAbsoluteUrl(business.logoUrl),
    toAbsoluteUrl(business.coverUrl),
    ...itemImages,
  ]);
}

function minutesToTime(value: number): string {
  const safe = Math.max(0, Math.min(1439, Math.round(value)));
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function buildOpeningHoursDay(day: BusinessHoursDay) {
  if (!day.isOpen) return null;

  if (
    typeof day.openMinutes !== "number" ||
    typeof day.closeMinutes !== "number"
  ) {
    return null;
  }

  if (day.openMinutes === day.closeMinutes) {
    return {
      opens: "00:00",
      closes: "23:59",
    };
  }

  return {
    opens: minutesToTime(day.openMinutes),
    closes: minutesToTime(day.closeMinutes),
  };
}

function buildOpeningHoursSpecification(business: Business) {
  if (!business.businessHours) return [];

  const hours = sanitizeBusinessHours(business.businessHours);
  if (hours.temporarilyClosed) return [];

  if (hours.is24Hours) {
    return BUSINESS_HOURS_DAYS.map((day) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: day.label,
      opens: "00:00",
      closes: "23:59",
    }));
  }

  return BUSINESS_HOURS_DAYS.flatMap((day) => {
    const openDay = buildOpeningHoursDay(hours.weekly[day.key]);
    if (!openDay) return [];

    return [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: day.label,
        ...openDay,
      },
    ];
  });
}

function buildPostalAddress(business: Business) {
  const pin = business.locationPin;
  const address = compactObject({
    "@type": "PostalAddress",
    streetAddress: firstString(business.addressLine, pin?.formattedAddress),
    addressLocality: firstString(
      business.cityName,
      business.location?.cityName,
      business.location?.citySuggestionName
    ),
    addressRegion: firstString(business.countyName, business.location?.countyName),
    addressCountry: firstString(business.countryCode, business.countryName),
  });

  return Object.keys(address).length > 1 ? address : null;
}

function buildGeo(business: Business) {
  const lat = business.locationPin?.lat;
  const lng = business.locationPin?.lng;

  if (typeof lat !== "number" || !Number.isFinite(lat)) return null;
  if (typeof lng !== "number" || !Number.isFinite(lng)) return null;

  return {
    "@type": "GeoCoordinates",
    latitude: lat,
    longitude: lng,
  };
}

export function buildCanonicalUrl(path: string): string {
  const normalizedPath = normalizePath(path);

  return `${WEB_BASE_URL}${normalizedPath === "/" ? "/" : normalizedPath}`;
}

export function buildOrganizationJsonLd() {
  return compactObject({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Soko Grid",
    legalName: "Soko Grid Ltd",
    url: SOKO_GRID_WEBSITE_URL,
    logo: SEO_DEFAULT_IMAGE_URL,
    contactPoint: compactObject({
      "@type": "ContactPoint",
      telephone: SOKO_GRID_PHONE_DISPLAY,
      email: SOKO_GRID_EMAIL,
      contactType: "customer support",
      areaServed: "KE",
    }),
  });
}

export function buildWebSiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Soko Grid",
    url: SOKO_GRID_WEBSITE_URL,
    description: SEO_HOME_DESCRIPTION,
    publisher: {
      "@type": "Organization",
      name: "Soko Grid Ltd",
      url: SOKO_GRID_WEBSITE_URL,
    },
  };
}

export function buildBusinessProfileSeo(business: Business, slug: string) {
  const cleanSlug = cleanString(slug || business.slug);
  const canonicalUrl = buildCanonicalUrl(`/p/${encodeURIComponent(cleanSlug)}`);
  const name = getBusinessName(business);
  const description = getBusinessMetaDescription(business);
  const images = getBusinessImages(business);
  const realDescription = getBusinessDescription(business);
  const address = buildPostalAddress(business);
  const geo = buildGeo(business);
  const openingHoursSpecification = buildOpeningHoursSpecification(business);
  const logo = toAbsoluteUrl(business.logoUrl);

  const jsonLd = compactObject({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name,
    url: canonicalUrl,
    description: realDescription,
    image: images,
    logo,
    telephone: firstString(business.phone),
    address,
    geo,
    hasMap: firstString(business.mapUrl),
    openingHoursSpecification,
  });

  return {
    title: `${name} | Soko Grid`,
    description,
    canonicalUrl,
    imageUrl: images[0] || SEO_DEFAULT_IMAGE_URL,
    openGraphType: "website" as const,
    jsonLd,
  };
}

function buildSearchLandingDescription(landing: PublicSearchLanding): string {
  const location = landing.locationLabel;
  const query = landing.queryLabel.toLowerCase();
  const count = landing.businesses.length;
  const countText =
    count === 1 ? "1 approved public business" : `${count} approved public businesses`;

  return truncateDescription(
    `Find ${query} in ${location} on Soko Grid. Browse ${countText} with public profile links, categories, descriptions, and locations.`
  );
}

export function buildSearchLandingSeo(landing: PublicSearchLanding) {
  const title = `${landing.queryLabel} in ${landing.locationLabel} | Soko Grid`;
  const description = buildSearchLandingDescription(landing);
  const canonicalUrl = buildCanonicalUrl(
    `/s/${encodeURIComponent(landing.slug)}`
  );

  const itemListElement = landing.businesses.map((business, index) => {
    const profileUrl = buildCanonicalUrl(
      `/p/${encodeURIComponent(business.slug)}`
    );

    return {
      "@type": "ListItem",
      position: index + 1,
      url: profileUrl,
    };
  });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${landing.queryLabel} in ${landing.locationLabel}`,
    url: canonicalUrl,
    numberOfItems: landing.businesses.length,
    itemListElement,
  };

  return {
    title,
    description,
    canonicalUrl,
    imageUrl: landing.businesses
      .map((business) => toAbsoluteUrl(business.logoUrl))
      .find(Boolean) || SEO_DEFAULT_IMAGE_URL,
    openGraphType: "website" as const,
    jsonLd,
  };
}
